import { useState, useEffect } from 'react';
import {
  getCartCookieOnClient,
  getPPIDAttributes
} from '@/lib/shopify/cart/utils';
import { createCartAndSetCookie } from '@/components/commerce/cart/actions';
import { getCart } from '@/lib/shopify';
import { logCartEvent } from '@/lib/cart/logCartEvent';
import { recoverFromStaleServerAction } from '@/lib/errors/staleServerAction';

// BL-997: the request never reached the server ("Load failed" on iOS Safari,
// "Failed to fetch" on Chrome). Shopify never said the cart is gone, so it is
// worth asking again before replacing it.
const NETWORK_ERROR_PATTERN = /Load failed|Failed to fetch|NetworkError|network error/i;
const GET_CART_RETRY_DELAY_MS = 1000;

function isNetworkError(error) {
  return error instanceof TypeError && NETWORK_ERROR_PATTERN.test(error.message);
}

/**
 * This hook ensures that the cart is loaded and available for use, and provides
 * a function to update it after you make changes. It reads the cart by checking
 * for a cart cookie, and if it doesn't find one, it creates a new cart and
 * sets the cookie. A network failure reading an existing cart is retried once
 * before falling back to a new cart.
 */
export default function useShopifyCart() {
  const [cart, setCart] = useState(null);
  useEffect(() => {
    const getInitialCart = async () => {
      // Declared outside the try so the fallback below can reuse it; otherwise
      // the retry drops PPID attribution.
      let trackingAttributes = [];
      let retried = false;
      try {
        trackingAttributes = getPPIDAttributes();
        const checkoutId = getCartCookieOnClient();

        if (checkoutId) {
          let initialCart;
          try {
            initialCart = await getCart(checkoutId, trackingAttributes);
          } catch (error) {
            // Replacing the cart here would orphan the shopper's items (the
            // cookie is overwritten), so retry once. A second failure falls
            // through to the fallback below, as before.
            if (!isNetworkError(error)) throw error;
            retried = true;
            await new Promise((resolve) => setTimeout(resolve, GET_CART_RETRY_DELAY_MS));
            initialCart = await getCart(checkoutId, trackingAttributes);
            if (initialCart) {
              logCartEvent(
                'CART_INIT_RETRY_RECOVERED',
                { error: error.message, checkoutId },
                'info',
              );
            }
          }
          // null means Shopify says the cart no longer exists (expired or
          // completed), the only case where a new cart is the right answer.
          if (!initialCart) {
            const newCart = await createCartAndSetCookie(trackingAttributes);
            setCart(newCart);
          } else {
            setCart(initialCart);
          }
        } else {
          const newCart = await createCartAndSetCookie(trackingAttributes);
          setCart(newCart);
        }
      } catch (error) {
        // Logged before recovering so RUM keeps seeing this error class. The
        // reload replaces the outdated bundle, so skip the fallback below,
        // which would call the same missing action and fail the same way.
        console.error('Error initializing cart. Creating new one:', error);
        const failedCheckoutId = getCartCookieOnClient();
        logCartEvent(
          'CART_INITIALIZATION_ERROR',
          {
            error: error.message,
            stack: error.stack,
            checkoutId: failedCheckoutId,
            had_cart_cookie: Boolean(failedCheckoutId),
            retried,
            fallback_action: 'creating_new_cart',
          },
          'warn',
          { keepalive: true },
        );
        if (recoverFromStaleServerAction(error, { stage: 'cart_init' })) return;

        try {
          const newCart = await createCartAndSetCookie(trackingAttributes);
          setCart(newCart);
        } catch (createError) {
          console.error('Failed to create fallback cart:', createError);
          logCartEvent(
            'CREATE_FALLBACK_CART_FAILED',
            { error: createError.message, stack: createError.stack },
            'error',
            { keepalive: true },
          );
          recoverFromStaleServerAction(createError, { stage: 'cart_fallback' });
        }
      }
    };
    getInitialCart();
  }, []);

  return [cart, setCart];
}
